import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Snackbar,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  InputAdornment,
  Menu,
  MenuItem as MenuItemComponent,
} from '@mui/material';

import {
  Computer as WindowsIcon,
  Storage as LinuxIcon,
  People as UsersIcon,
  Add as AddIcon,
  Launch as ConnectIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Terminal as SSHIcon,
  Key as KeyIcon,
  Upload as UploadIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

import { useAuth } from '../contexts/AuthContext';

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

// Helper function to get auth headers with Cognito token
const getAuthHeaders = () => {
  const token = localStorage.getItem('cognito_token');
  const headers = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

const RemoteConnections = () => {
  const { user, isAuthenticated } = useAuth();

  const [connections, setConnections] = useState([]);
  const [guacUsers, setGuacUsers] = useState([]);
  const [currentUserBackend, setCurrentUserBackend] = useState(null);

  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Table pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [sortBy, setSortBy] = useState('created_at'); // field to sort by

  // Edit state
  const [editingConnection, setEditingConnection] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedConnection, setSelectedConnection] = useState(null);

  // Computed functions for filtering and sorting - MOVED TO TOP
  const filteredAndSortedConnections = useMemo(() => {
    // Safety check: if connections is undefined or not an array, return empty array
    if (!connections || !Array.isArray(connections)) {
      return [];
    }

    let filtered = connections;

    // Apply search filter with null/undefined checks
    if (searchQuery) {
      filtered = filtered.filter(conn => 
        conn && (
          (conn.name && conn.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (conn.protocol && conn.protocol.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (conn.hostname && conn.hostname.toLowerCase().includes(searchQuery.toLowerCase()))
        )
      );
    }

    // Apply sorting (latest connections first by default) with null/undefined checks
    return filtered.sort((a, b) => {
      // Safety check for connection objects
      if (!a || !b) return 0;
      
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle null/undefined values
      if (aValue === undefined || aValue === null) aValue = '';
      if (bValue === undefined || bValue === null) bValue = '';

      // Handle string comparison
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [connections, searchQuery, sortBy, sortOrder]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc'); // default to desc for new field
    }
  };

  // Menu handlers
  const handleMenuOpen = (event, connection) => {
    setMenuAnchor(event.currentTarget);
    setSelectedConnection(connection);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedConnection(null);
  };

  // Edit handlers
  const handleEdit = (connection) => {
    setEditingConnection(connection);
    setOpenEditDialog(true);
    handleMenuClose();
  };

  const handleDelete = async (connection) => {
    if (window.confirm(`Are you sure you want to delete "${connection.name}"?`)) {
      try {
        const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
        const response = await fetch(`${API_BASE}/remote-connections/connections/${connection.identifier}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            username: user?.username
          }),
        });

        if (response.ok) {
          setSnackbar({
            open: true,
            message: 'Connection deleted successfully',
            severity: 'success',
          });
          fetchRemoteData(); // Refresh the list
        } else {
          throw new Error('Failed to delete connection');
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to delete connection',
          severity: 'error',
        });
      }
    }
    handleMenuClose();
  };

  const handleUpdateConnection = async () => {
    if (!editingConnection) return;

    setCreatingConnection(true);
    try {
      const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
      
      // Prepare connection data in Guacamole API format
      const connectionData = {
        name: editingConnection.name,
        identifier: editingConnection.identifier,
        parentIdentifier: "ROOT",
        protocol: editingConnection.protocol,
        attributes: {
          "guacd-encryption": "",
          "failover-only": "",
          "weight": "",
          "max-connections": "",
          "guacd-hostname": null,
          "guacd-port": "",
          "max-connections-per-user": ""
        },
        activeConnections: 0,
        lastActive: Date.now(),
        parameters: {
          hostname: editingConnection.hostname,
          port: editingConnection.port,
          username: editingConnection.username,
          "color-scheme": "",
          "font-size": "",
          "scrollback": "",
          "read-only": "",
          "disable-copy": "",
          "disable-paste": "",
          "timezone": null,
          "server-alive-interval": "",
          "backspace": "",
          "terminal-type": "",
          "create-typescript-path": "",
          "recording-exclude-output": "",
          "recording-exclude-mouse": "",
          "recording-include-keys": "",
          "create-recording-path": "",
          "enable-sftp": "",
          "sftp-disable-download": "",
          "sftp-disable-upload": "",
          "wol-send-packet": "",
          "wol-udp-port": "",
          "wol-wait-time": ""
        },
      };

      // Add authentication method
      if (editingConnection.protocol === 'ssh') {
        if (editingConnection.privateKey) {
          connectionData.parameters['private-key'] = editingConnection.privateKey;
          connectionData.parameters['enable-sftp'] = "true"; // Auto-enable SFTP for private key
        } else if (editingConnection.password) {
          connectionData.parameters.password = editingConnection.password;
        }
      } else if (editingConnection.protocol === 'rdp') {
        connectionData.parameters.password = editingConnection.password;
      }

      const response = await fetch(`${API_BASE}/remote-connections/connections/${editingConnection.identifier}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          connection_data: connectionData,
          username: user?.username
        }),
      });

      if (response.ok) {
        setSnackbar({
          open: true,
          message: 'Connection updated successfully',
          severity: 'success',
        });
        setOpenEditDialog(false);
        setEditingConnection(null);
        fetchRemoteData(); // Refresh the list
      } else {
        throw new Error('Failed to update connection');
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Failed to update connection',
        severity: 'error',
      });
    } finally {
      setCreatingConnection(false);
    }
  };

  // User creation
  const [openCreateUserDialog, setOpenCreateUserDialog] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    new_username: '',
    full_name: '',
    email: '',
    organization: '',
    organizational_role: '',
  });

  // Connection creation
  const [openCreateConnectionDialog, setOpenCreateConnectionDialog] = useState(false);
  const [creatingConnection, setCreatingConnection] = useState(false);
  const [newConnectionForm, setNewConnectionForm] = useState({
    name: '',
    protocol: 'ssh',
    hostname: '',
    port: '22',
    username: '',
    password: '',
    privateKey: '',
  });

  const fetchRemoteData = useCallback(async () => {
    if (!isAuthenticated || !user?.email || !user?.username) {
      setErrorMsg('Authentication required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setErrorMsg(null);

    try {
      // 1. Sync / Create Remote User
      const remoteUserRes = await fetch(
        `${API_BASE}/remote-connections/remote-user`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            sub: user.sub,
            email: user.email,
            username: user.username,
            email_verified: user.email_verified ?? false,
          }),
        }
      );

      const remoteUserData = await remoteUserRes.json();
      if (!remoteUserRes.ok) {
        throw new Error(remoteUserData.detail || 'User sync failed');
      }

      setCurrentUserBackend(remoteUserData.user);

      // 2. Ensure Active Guac Session
      const authRes = await fetch(
        `${API_BASE}/remote-connections/guac-auth`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ username: user.username }),
        }
      );

      const authData = await authRes.json();
      if (!authRes.ok) {
        throw new Error(authData.detail || 'Guacamole authentication failed');
      }

      // 3. Fetch User-Specific Connections
      const treeRes = await fetch(
        `${API_BASE}/remote-connections/user-connections`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ username: user.username }),
        }
      );

      const treeData = await treeRes.json();
      if (!treeRes.ok) {
        const errorMsg = treeData.detail || treeData.message || 'Failed to load your connections';
        if (treeRes.status === 403) {
          throw new Error(`${errorMsg}. Please click "Refresh Session" to fix authentication.`);
        } else if (treeRes.status === 401) {
          throw new Error(`${errorMsg}. Please check your authentication.`);
        } else {
          throw new Error(errorMsg);
        }
      }

      const connectionsList =
        treeData.tree?.childConnections
          ? Object.values(treeData.tree.childConnections)
          : [];

      // Sort connections by creation date (latest first) - fallback to name if no date
      const sortedConnections = connectionsList.sort((a, b) => {
        // Try to sort by creation date if available
        if (a.created_at && b.created_at) {
          return new Date(b.created_at) - new Date(a.created_at);
        }
        // Fallback to sorting by name if no dates
        return a.name.localeCompare(b.name);
      });

      setConnections(sortedConnections);

      // 4. Fetch Guacamole Users
      const usersRes = await fetch(
        `${API_BASE}/remote-connections/guac-users/list`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ username: user.username }),
        }
      );

      const usersData = await usersRes.json();
      if (!usersRes.ok) {
        throw new Error(usersData.detail || 'Failed to load users');
      }

      const usersList = usersData.users
        ? Object.values(usersData.users)
        : [];

      setGuacUsers(usersList);
    } catch (err) {
      console.error('Remote workspace error:', err);
      setErrorMsg(err.message || 'Failed to load remote data');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  React.useEffect(() => {
    fetchRemoteData();
  }, [fetchRemoteData]);

  const handleCreateUser = async () => {
    if (!newUserForm.new_username.trim()) {
      setSnackbar({ open: true, severity: 'warning', message: 'Username is required' });
      return;
    }

    setCreatingUser(true);
    try {
      const res = await fetch(`${API_BASE}/remote-connections/guac-users/create-user`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          caller_username: user.username,
          ...newUserForm,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || 'Failed to create user');

      setSnackbar({
        open: true,
        severity: 'success',
        message: `User created! Temporary password: ${data.temporary_password}`,
      });

      setOpenCreateUserDialog(false);
      setNewUserForm({
        new_username: '',
        full_name: '',
        email: '',
        organization: '',
        organizational_role: '',
      });

      await fetchRemoteData();
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setCreatingUser(false);
    }
  };

  const handleCreateConnection = async () => {
    if (!newConnectionForm.name.trim()) {
      setSnackbar({ open: true, severity: 'warning', message: 'Connection name is required' });
      return;
    }
    if (!newConnectionForm.hostname.trim()) {
      setSnackbar({ open: true, severity: 'warning', message: 'Hostname is required' });
      return;
    }
    if (!newConnectionForm.username.trim()) {
      setSnackbar({ open: true, severity: 'warning', message: 'Username is required' });
      return;
    }
    
    // For SSH: require at least password OR private key (or both)
    if (newConnectionForm.protocol === 'ssh') {
      const hasPassword = newConnectionForm.password.trim().length > 0;
      const hasPrivateKey = newConnectionForm.privateKey.trim().length > 0;
      
      if (!hasPassword && !hasPrivateKey) {
        setSnackbar({ 
          open: true, 
          severity: 'warning', 
          message: 'For SSH connections, please provide either a password, a private key, or both' 
        });
        return;
      }
      
      // Validate private key format if provided
      if (hasPrivateKey) {
        if (!newConnectionForm.privateKey.includes('-----BEGIN') || !newConnectionForm.privateKey.includes('-----END')) {
          setSnackbar({ 
            open: true, 
            severity: 'warning', 
            message: 'Invalid private key format. Must include BEGIN and END markers' 
          });
          return;
        }
      }
    }
    
    // For RDP: password is required
    if (newConnectionForm.protocol === 'rdp' && !newConnectionForm.password.trim()) {
      setSnackbar({ open: true, severity: 'warning', message: 'Password is required for RDP connections' });
      return;
    }

    setCreatingConnection(true);

    try {
      const parameters = {
        hostname: newConnectionForm.hostname,
        port: newConnectionForm.port,
        username: newConnectionForm.username,
      };

      // Add password if provided
      if (newConnectionForm.password.trim()) {
        parameters.password = newConnectionForm.password;
      }
      
      // Add private key if provided
      if (newConnectionForm.privateKey.trim()) {
        parameters['private-key'] = newConnectionForm.privateKey;
      }

      const payload = {
        username: user.username,
        connection_request: {
          name: newConnectionForm.name,
          protocol: newConnectionForm.protocol,
          parameters: parameters,
          attributes: {},
          created_by: user.username,
        },
      };

      const res = await fetch(`${API_BASE}/remote-connections/connections`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        // Handle structured error responses
        let errorMessage = 'Failed to create connection';
        
        // Check if error is nested under 'detail' (FastAPI HTTPException format)
        const errorData = data.detail || data;
        
        if (errorData.error) {
          if (errorData.error === 'duplicate_connection' && errorData.key) {
            errorMessage = `Duplicate connection: ${errorData.key}`; // Clear duplicate message
          } else {
            errorMessage = errorData.error; // Show the error type
          }
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else {
          // Fallback to stringified version
          errorMessage = JSON.stringify(errorData);
        }
        
        throw new Error(errorMessage);
      }

      const proto = newConnectionForm.protocol.toUpperCase();
      const authMethods = [];
      if (newConnectionForm.password.trim()) authMethods.push('Password');
      if (newConnectionForm.privateKey.trim()) authMethods.push('Private Key');
      const authMethodText = authMethods.length > 0 ? ` (${authMethods.join(' + ')})` : '';
      
      setSnackbar({
        open: true,
        severity: 'success',
        message: `${proto} connection '${data.connection?.name || newConnectionForm.name}'${authMethodText} created successfully!`,
      });

      setOpenCreateConnectionDialog(false);

      setNewConnectionForm({
        name: '',
        protocol: 'ssh',
        hostname: '',
        port: '22',
        username: '',
        password: '',
        privateKey: '',
      });

      await fetchRemoteData();
    } catch (err) {
      console.error('Connection creation error:', err);
      setSnackbar({ open: true, severity: 'error', message: err.message });
    } finally {
      setCreatingConnection(false);
    }
  };

  const handleProtocolChange = (e) => {
    const newProtocol = e.target.value;
    setNewConnectionForm((prev) => ({
      ...prev,
      protocol: newProtocol,
      port: newProtocol === 'rdp' ? '3389' : '22',
      // Clear credentials when switching protocols for security
      password: '',
      privateKey: '',
    }));
  };

  const handlePrivateKeyFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        // Basic validation for private key format
        if (content.includes('-----BEGIN') && content.includes('-----END')) {
          setNewConnectionForm(prev => ({
            ...prev,
            privateKey: content
          }));
        } else {
          setSnackbar({ 
            open: true, 
            severity: 'error', 
            message: 'Invalid private key file. Please upload a valid PEM format private key.' 
          });
        }
      };
      reader.onerror = () => {
        setSnackbar({ 
          open: true, 
          severity: 'error', 
          message: 'Failed to read private key file.' 
        });
      };
      reader.readAsText(file);
    }
  };

  const handleEditPrivateKeyFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target.result;
        // Basic validation for private key format
        if (content.includes('-----BEGIN') && content.includes('-----END')) {
          setEditingConnection(prev => ({
            ...prev,
            privateKey: content
          }));
        } else {
          setSnackbar({ 
            open: true, 
            severity: 'error', 
            message: 'Invalid private key file. Please upload a valid PEM format private key.' 
          });
        }
      };
      reader.onerror = () => {
        setSnackbar({ 
          open: true, 
          severity: 'error', 
          message: 'Failed to read private key file.' 
          });
      };
      reader.readAsText(file);
    }
  };

  const handleRefreshSession = async () => {
    try {
      // Clear any existing cached auth
      localStorage.removeItem('GUAC_AUTH');
      
      // Force a fresh authentication
      const res = await fetch(`${API_BASE}/remote-connections/guac-auth`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ username: user.username }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || 'Failed to refresh session');

      // Store the new auth token
      if (data.session && data.session.auth_token) {
        localStorage.setItem('GUAC_AUTH', JSON.stringify(data.session));
      }

      setSnackbar({
        open: true,
        severity: 'success',
        message: 'Session refreshed successfully! Try accessing connections now.',
      });

      // Refresh connections data
      await fetchRemoteData();
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const handleFixPermissions = async () => {
    try {
      const res = await fetch(`${API_BASE}/remote-connections/fix-connection-permissions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ username: user.username }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || 'Failed to fix permissions');

      setSnackbar({
        open: true,
        severity: 'success',
        message: `Fixed permissions for ${data.fixed_count} connections. Try connecting again!`,
      });

      // Refresh connections data
      await fetchRemoteData();
    } catch (err) {
      setSnackbar({ open: true, severity: 'error', message: err.message });
    }
  };

  const fixConnectionOwnership = async () => {
    try {
      setSnackbar({
        open: true,
        severity: 'info',
        message: 'Cleaning up invalid connections...',
      });

      const res = await fetch(`${API_BASE}/remote-connections/cleanup-invalid-connections`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ username: user.username }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to cleanup connections');
      }

      console.log('DEBUG: Connection cleanup result:', data);

      const { removed_count, invalid_connections, guacamole_cleanup_needed, cleanup_sql } = data;

      if (removed_count === 0) {
        setSnackbar({
          open: true,
          severity: 'success',
          message: 'All connections are valid! No cleanup needed.',
        });
      } else {
        const message = guacamole_cleanup_needed 
          ? `Removed ${removed_count} invalid connections. You may need to run SQL cleanup in Guacamole database. Check console for commands.`
          : `Removed ${removed_count} invalid connections (${invalid_connections.join(', ')}). Refreshing connection list...`;
        
        setSnackbar({
          open: true,
          severity: guacamole_cleanup_needed ? 'warning' : 'success',
          message,
        });

        // Show cleanup SQL in console if needed
        if (guacamole_cleanup_needed && cleanup_sql) {
          console.log('GUACAMOLE DATABASE CLEANUP NEEDED:');
          console.log(cleanup_sql);
          console.log('Run these SQL commands in your Guacamole PostgreSQL database to fix foreign key constraint errors.');
        }
        
        // Refresh connections data
        await fetchRemoteData();
      }
    } catch (error) {
      setSnackbar({
        open: true,
        severity: 'error',
        message: `Failed to cleanup connections: ${error.message}`,
      });
    }
  };

  const fixConnection = async (conn) => {
    try {
      // Add basic SSH parameters as a temporary fix
      const basicParams = {
        hostname: 'your-server-ip',  // User will need to update this
        port: '22',
        username: 'your-username',  // User will need to update this
        password: 'your-password',  // User will need to update this
      };

      const res = await fetch(`${API_BASE}/remote-connections/update-connection-parameters`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          username: user.username, 
          connection_id: conn.identifier,
          parameters: basicParams
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to fix connection');
      }

      setSnackbar({
        open: true,
        severity: 'success',
        message: `Added basic parameters to ${conn.name}. Please edit the connection to update hostname, username, and password.`,
      });

      // Refresh connections data
      await fetchRemoteData();
    } catch (error) {
      setSnackbar({ 
        open: true, 
        severity: 'error', 
        message: `Failed to fix connection: ${error.message}` 
      });
    }
  };

  const generateGuacUrl = (connectionId, token, datasource = 'postgresql') => {
    // Guacamole's SPA route expects an *encoded client identifier*, not the raw numeric ID.
    // The identifier is built as: "<connectionId>\0c\0<datasource>" then base64-encoded.
    // Example (from Guacamole UI): "MQBjAHBvc3RncmVzcWw" decodes to bytes for "1\0c\0postgresql".
    const clientIdentifier = `${String(connectionId)}\u0000c\u0000${datasource}`;
    const encodedClientIdentifier = btoa(clientIdentifier);

    console.log('DEBUG: Guac clientIdentifier (raw):', clientIdentifier);
    console.log('DEBUG: Guac clientIdentifier (b64):', encodedClientIdentifier);

    const guacUrl = `https://remote-access.cloudsanalytics.ai/guacamole/#/client/${encodedClientIdentifier}?token=${token}`;
    console.log(`DEBUG: Generated Guacamole URL: ${guacUrl}`);
    return guacUrl;
  };

  const handleConnect = async (conn) => {
    try {
      console.log(`DEBUG: Attempting to connect to ${conn.name} (ID: ${conn.identifier})`);
      
      // First check if user has permissions for this connection
      console.log('DEBUG: Checking connection permissions...');
      const permRes = await fetch(`${API_BASE}/remote-connections/check-connection-permissions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          username: user.username, 
          connection_id: conn.identifier 
        }),
      });

      const permData = await permRes.json();
      console.log('DEBUG: Permission check result:', permData);
      
      if (!permRes.ok) {
        throw new Error(`Permission check failed: ${permData.detail || permData.message}`);
      }

      if (!permData.has_permission) {
        console.log('DEBUG: User does not have permission, trying to fix permissions...');
        // Try to fix permissions automatically
        const fixRes = await fetch(`${API_BASE}/remote-connections/fix-connection-permissions`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({ username: user.username }),
        });

        const fixData = await fixRes.json();
        console.log('DEBUG: Fix permissions result:', fixData);
        
        if (fixRes.ok && fixData.fixed_count > 0) {
          setSnackbar({
            open: true,
            severity: 'info',
            message: `Fixed permissions for ${fixData.fixed_count} connections. Please try connecting again.`,
          });
          return;
        }
      }
      
      // Get fresh authentication token
      console.log('DEBUG: Getting fresh auth token...');
      const res = await fetch(`${API_BASE}/remote-connections/guac-auth`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ username: user.username }),
      });

      const data = await res.json();
      console.log('DEBUG: Auth response:', data);
      
      if (!res.ok) {
        throw new Error(data.detail || data.message || 'Failed to get authentication token');
      }

      if (data.session && data.session.auth_token) {
        const token = data.session.auth_token;
        const datasource = data.session.datasource || 'postgresql';
        const guacUrl = generateGuacUrl(conn.identifier, token, datasource);

        console.log(`DEBUG: Final Guacamole URL: ${guacUrl}`);
        console.log(`DEBUG: Token preview: ${token.substring(0, 20)}...`);
        console.log(`DEBUG: Datasource: ${datasource}`);
        console.log(`DEBUG: Connection details:`, conn);
        
        // Check if connection has required fields
        const requiredFields = ['protocol', 'name', 'identifier'];
        const missingFields = requiredFields.filter(field => !conn[field]);
        if (missingFields.length > 0) {
          console.warn(`WARNING: Connection missing required fields: ${missingFields}`);
        }
        
        // Check connection parameters
        if (conn.parameters) {
          console.log(`DEBUG: Connection parameters:`, conn.parameters);
          const protocol = conn.protocol?.toLowerCase();
          const requiredParams = protocol === 'rdp' ? ['hostname', 'port', 'username'] : ['hostname', 'port'];
          const missingParams = requiredParams.filter(param => !conn.parameters[param]);
          if (missingParams.length > 0) {
            console.warn(`WARNING: Connection missing required parameters: ${missingParams}`);
          }
        } else {
          console.warn(`WARNING: Connection has no parameters - fetching full details...`);
          
          // Fetch full connection details from Guacamole API
          try {
            const detailRes = await fetch(`${API_BASE}/remote-connections/get-connection-details`, {
              method: 'POST',
              headers: getAuthHeaders(),
              body: JSON.stringify({ 
                username: user.username, 
                connection_id: conn.identifier 
              }),
            });

            const detailData = await detailRes.json();
            console.log('DEBUG: Full connection details from API:', detailData);
            
            if (detailRes.ok && detailData.connection) {
              const fullConn = detailData.connection;
              console.log(`DEBUG: Full connection object:`, fullConn);
              console.log(`DEBUG: Full connection parameters:`, fullConn.parameters);
              
              // Check if parameters exist either as 'parameters' field or at root level
              const hasParameters = fullConn.parameters || 
                                   (fullConn.hostname && fullConn.port && fullConn.username);
              
              console.log(`DEBUG: Has parameters:`, hasParameters);
              
              // Get the actual parameters object
              const actualParams = fullConn.parameters || fullConn;
              console.log(`DEBUG: Actual parameters:`, actualParams);
              
              // Check if parameters are still missing
              if (!hasParameters || !actualParams.hostname) {
                setSnackbar({
                  open: true,
                  severity: 'error',
                  message: `Connection ${conn.name} has no configuration. You need to add server details (hostname, port, credentials) to connect.`,
                  action: (
                    <Button color="secondary" size="small" onClick={() => fixConnection(conn)}>
                      Quick Fix
                    </Button>
                  )
                });
                return;
              } else {
                console.log(`DEBUG: ✓ Connection has parameters, proceeding to connect`);
                // Update the connection object with parameters for the connection logic
                conn.parameters = actualParams;
              }
            }
          } catch (error) {
            console.error('Failed to fetch connection details:', error);
          }
        }
        
        // Store the fresh token for future use
        localStorage.setItem('GUAC_AUTH', JSON.stringify(data.session));
        
        console.log(`Opening connection to ${conn.name} (${conn.identifier})`);
        window.open(guacUrl, '_blank');
        
        setSnackbar({
          open: true,
          severity: 'success',
          message: `Opening connection to ${conn.name}...`,
        });
      } else {
        throw new Error('No authentication token received');
      }
    } catch (error) {
      console.error('Failed to connect:', error);
      setSnackbar({
        open: true,
        severity: 'error',
        message: `Failed to connect to ${conn.name}: ${error.message}`,
      });
    }
  };

  const getProtocolColor = (protocol) => {
    const p = protocol?.toLowerCase();
    if (p === 'rdp') return 'primary';
    if (p === 'ssh') return 'success';
    if (p === 'vnc') return 'secondary';
    return 'default';
  };

  const getProtocolIcon = (protocol) => {
    const p = protocol?.toLowerCase();
    switch (p) {
      case 'rdp':
        return <WindowsIcon color="primary" />;
      case 'ssh':
        return <SSHIcon color="primary" />;
      case 'vnc':
        return <LinuxIcon color="action" />;
      default:
        return <LinuxIcon color="action" />;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          height: '80vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          gap: 3,
        }}
      >
        <CircularProgress size={70} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Loading your remote workspace...
        </Typography>
      </Box>
    );
  }

  if (errorMsg) {
    return (
      <Box sx={{ p: { xs: 3, md: 6 }, maxWidth: 900, mx: 'auto' }}>
        <Alert severity="error" variant="filled" sx={{ borderRadius: 3 }}>
          {errorMsg}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', p: { xs: 3, md: 5, lg: 6 } }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 6 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="primary.dark">
            Remote Access Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage your remote connections
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          <TextField
            placeholder="Search connections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateConnectionDialog(true)}
            sx={{ borderRadius: 3, textTransform: 'none', px: 3 }}
          >
            New Connection
          </Button>
        </Stack>
      </Stack>

      {/* Connections Table */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: 'text.primary' }}>
        Your Connections
      </Typography>

      {connections.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'grey.50',
            borderRadius: 3,
            border: '2px dashed',
            borderColor: 'grey.300',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No connections found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Create a new connection to get started.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenCreateConnectionDialog(true)}
          >
            Add Your First Connection
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'primary.main' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 600, py: 2, width: '80px' }}>
                    #
                  </TableCell>
                  <TableCell 
                    sx={{ color: 'white', fontWeight: 600, py: 2, cursor: 'pointer' }}
                    onClick={() => handleSort('name')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Connection Name
                      {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </Box>
                  </TableCell>
                  <TableCell 
                    sx={{ color: 'white', fontWeight: 600, py: 2, cursor: 'pointer' }}
                    onClick={() => handleSort('protocol')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      Protocol
                      {sortBy === 'protocol' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600, py: 2 }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 600, py: 2, textAlign: 'center' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rowsPerPage > 0
                  ? filteredAndSortedConnections.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  : filteredAndSortedConnections
                ).map((conn, index) => (
                  <TableRow
                    key={conn.identifier}
                    sx={{
                      '&:hover': { bgcolor: 'grey.50' },
                      '&:nth-of-type(even)': { bgcolor: 'grey.50' },
                      '&:nth-of-type(even):hover': { bgcolor: 'grey.100' },
                    }}
                  >
                    <TableCell sx={{ py: 3 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        #{page * rowsPerPage + index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 3 }}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {conn.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Box sx={{ color: getProtocolColor(conn.protocol).replace('main', 'dark') }}>
                          {getProtocolIcon(conn.protocol)}
                        </Box>
                        <Chip
                          label={conn.protocol?.toUpperCase() || 'Unknown'}
                          color={getProtocolColor(conn.protocol)}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Connect to this connection">
                          <Button
                            variant="contained"
                            size="small"
                            endIcon={<ConnectIcon />}
                            onClick={() => handleConnect(conn)}
                            sx={{ borderRadius: 2, textTransform: 'none' }}
                          >
                            Connect
                          </Button>
                        </Tooltip>
                        <Tooltip title="More options">
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, conn)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
            component="div"
            count={filteredAndSortedConnections.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{ 
              borderTop: '1px solid rgba(224, 224, 224, 1)',
              '.MuiTablePagination-toolbar': {
                minHeight: '56px',
              }
            }}
          />
        </Paper>
      )}


      {/* Create Connection Dialog - FULLY UPDATED */}
      <Dialog open={openCreateConnectionDialog} onClose={() => setOpenCreateConnectionDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle variant="h5">
          Add New {newConnectionForm.protocol.toUpperCase()} Connection
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            {/* Protocol Selector */}
            <FormControl fullWidth required>
              <InputLabel>Protocol</InputLabel>
              <Select
                value={newConnectionForm.protocol}
                label="Protocol"
                onChange={handleProtocolChange}
              >
                <MenuItem value="ssh">SSH (Linux/Server)</MenuItem>
                <MenuItem value="rdp">RDP (Windows Desktop)</MenuItem>
              </Select>
            </FormControl>

            <TextField
              required
              label="Connection Name"
              fullWidth
              value={newConnectionForm.name}
              onChange={(e) => setNewConnectionForm({ ...newConnectionForm, name: e.target.value })}
              placeholder="e.g., My Windows PC / Production Server"
            />

            <TextField
              required
              label="Hostname / IP Address"
              fullWidth
              value={newConnectionForm.hostname}
              onChange={(e) => setNewConnectionForm({ ...newConnectionForm, hostname: e.target.value })}
              placeholder="e.g., 192.168.1.100 or 52.66.208.202 or server.example.com"
            />

            <TextField
              required
              label="Port"
              fullWidth
              type="number"
              value={newConnectionForm.port}
              onChange={(e) => setNewConnectionForm({ ...newConnectionForm, port: e.target.value })}
              placeholder={newConnectionForm.protocol === 'rdp' ? '3389' : '22'}
              helperText={
                newConnectionForm.protocol === 'rdp'
                  ? 'Default for RDP / Windows Remote Desktop'
                  : 'Default for SSH'
              }
            />

            <TextField
              required
              label="Username"
              fullWidth
              value={newConnectionForm.username}
              onChange={(e) => setNewConnectionForm({ ...newConnectionForm, username: e.target.value })}
              placeholder={
                newConnectionForm.protocol === 'rdp'
                  ? 'e.g., Administrator or your Windows username'
                  : 'e.g., ubuntu, root, ec2-user'
              }
            />

            {/* Password Field - Required for RDP, Optional for SSH */}
            <TextField
              required={newConnectionForm.protocol === 'rdp'}
              label="Password"
              fullWidth
              type="password"
              value={newConnectionForm.password}
              onChange={(e) => setNewConnectionForm({ ...newConnectionForm, password: e.target.value })}
              placeholder="Enter your login password (optional for SSH)"
              helperText={
                newConnectionForm.protocol === 'ssh' 
                  ? 'Optional: Provide password as backup authentication method'
                  : 'Required: Enter your Windows password'
              }
            />

            {/* Private Key Field - Only for SSH */}
            {newConnectionForm.protocol === 'ssh' && (
              <>
                <TextField
                  label="Private Key"
                  fullWidth
                  multiline
                  rows={6}
                  value={newConnectionForm.privateKey}
                  onChange={(e) => setNewConnectionForm({ ...newConnectionForm, privateKey: e.target.value })}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;Your private key content here...&#10;-----END RSA PRIVATE KEY-----"
                  helperText="Optional: Paste your private key in PEM format or use the file upload below"
                />
                
                <Box>
                  <input
                    type="file"
                    accept=".pem,.key"
                    style={{ display: 'none' }}
                    id="private-key-upload"
                    onChange={handlePrivateKeyFileUpload}
                  />
                  <label htmlFor="private-key-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      sx={{ mt: 1 }}
                    >
                      Upload Private Key File
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Supported formats: .pem, .key files
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenCreateConnectionDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              creatingConnection ||
              !newConnectionForm.name.trim() ||
              !newConnectionForm.hostname.trim() ||
              !newConnectionForm.username.trim() ||
              (newConnectionForm.protocol === 'ssh' && 
               !newConnectionForm.password.trim() && 
               !newConnectionForm.privateKey.trim()) ||
              (newConnectionForm.protocol === 'rdp' && 
               !newConnectionForm.password.trim())
            }
            onClick={handleCreateConnection}
          >
            {creatingConnection
              ? 'Creating...'
              : `Create ${newConnectionForm.protocol.toUpperCase()} Connection`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu for Actions */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: {
            minWidth: 200,
            '& .MuiList-root': {
              padding: '8px',
            },
          },
        }}
      >
        <MenuItemComponent onClick={() => handleEdit(selectedConnection)}>
          <EditIcon sx={{ mr: 2, fontSize: 20 }} />
          Edit Connection
        </MenuItemComponent>
        <MenuItemComponent onClick={() => handleDelete(selectedConnection)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 2, fontSize: 20 }} />
          Delete Connection
        </MenuItemComponent>
      </Menu>

      {/* Edit Connection Dialog */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle variant="h5">
          Edit {editingConnection?.protocol?.toUpperCase()} Connection
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 3 }}>
          <Stack spacing={2.5}>
            <TextField
              required
              label="Connection Name"
              fullWidth
              value={editingConnection?.name || ''}
              onChange={(e) => setEditingConnection({ ...editingConnection, name: e.target.value })}
              placeholder="e.g., My Windows PC / Production Server"
            />

            <TextField
              required
              label="Hostname / IP Address"
              fullWidth
              value={editingConnection?.hostname || ''}
              onChange={(e) => setEditingConnection({ ...editingConnection, hostname: e.target.value })}
              placeholder="e.g., 192.168.1.100 or 52.66.208.202 or server.example.com"
            />

            <TextField
              required
              label="Port"
              fullWidth
              type="number"
              value={editingConnection?.port || ''}
              onChange={(e) => setEditingConnection({ ...editingConnection, port: e.target.value })}
              placeholder={editingConnection?.protocol === 'rdp' ? '3389' : '22'}
            />

            <TextField
              required
              label="Username"
              fullWidth
              value={editingConnection?.username || ''}
              onChange={(e) => setEditingConnection({ ...editingConnection, username: e.target.value })}
              placeholder={
                editingConnection?.protocol === 'rdp'
                  ? 'e.g., Administrator or your Windows username'
                  : 'e.g., ubuntu, root, ec2-user'
              }
            />

            {/* Password Field */}
            <TextField
              required={editingConnection?.protocol === 'rdp'}
              label="Password"
              fullWidth
              type="password"
              value={editingConnection?.password || ''}
              onChange={(e) => setEditingConnection({ ...editingConnection, password: e.target.value })}
              placeholder="Enter your login password"
              helperText={
                editingConnection?.protocol === 'ssh' 
                  ? 'Optional: Provide password as backup authentication method'
                  : 'Required: Enter your Windows password'
              }
            />

            {/* Private Key Field - Only for SSH */}
            {editingConnection?.protocol === 'ssh' && (
              <>
                <TextField
                  label="Private Key"
                  fullWidth
                  multiline
                  rows={6}
                  value={editingConnection?.privateKey || ''}
                  onChange={(e) => setEditingConnection({ ...editingConnection, privateKey: e.target.value })}
                  placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;Your private key content here...&#10;-----END RSA PRIVATE KEY-----"
                  helperText="Optional: Paste your private key in PEM format or use the file upload below"
                />
                
                <Box>
                  <input
                    type="file"
                    accept=".pem,.key"
                    style={{ display: 'none' }}
                    id="edit-private-key-upload"
                    onChange={handleEditPrivateKeyFileUpload}
                  />
                  <label htmlFor="edit-private-key-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                      sx={{ mt: 1 }}
                    >
                      Upload Private Key File
                    </Button>
                  </label>
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Supported formats: .pem, .key files
                  </Typography>
                </Box>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateConnection}
            disabled={
              !editingConnection?.name?.trim() ||
              !editingConnection?.hostname?.trim() ||
              !editingConnection?.username?.trim() ||
              (editingConnection?.protocol === 'ssh' && 
               !editingConnection?.password?.trim() && 
               !editingConnection?.privateKey?.trim()) ||
              (editingConnection?.protocol === 'rdp' && 
               !editingConnection?.password?.trim())
            }
          >
            Update Connection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RemoteConnections;